import { ChevronRight, ExpandMore } from '@mui/icons-material';
import { TreeItem, TreeView, useTreeItem } from '@mui/lab';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import clsx from 'clsx';
import React, { forwardRef, useCallback, useEffect, useState } from 'react';

const CustomContent = forwardRef(function CustomContent(props, ref) {
  const {
    classes,
    className,
    label,
    nodeId,
    icon: iconProp,
    expansionIcon,
    displayIcon,
  } = props;

  const {
    disabled,
    expanded,
    selected,
    focused,
    handleExpansion,
    handleSelection,
    preventSelection,
  } = useTreeItem(nodeId);

  const icon = iconProp || expansionIcon || displayIcon;

  const handleMouseDown = event => {
    preventSelection(event);
  };

  const handleExpansionClick = event => {
    handleExpansion(event);
    event.stopPropagation();
  };

  const handleSelectionClick = event => {
    handleSelection(event);
  };

  return (
    <div
      className={clsx(className, classes.root, {
        [classes.expanded]: expanded,
        [classes.selected]: selected,
        [classes.focused]: focused,
        [classes.disabled]: disabled,
      })}
      onMouseDown={handleMouseDown}
      ref={ref}
    >
      <div onClick={handleExpansionClick} className={classes.iconContainer}>
        {icon}
      </div>
      <Typography
        onClick={handleSelectionClick}
        component="div"
        className={classes.label}
      >
        {label}
      </Typography>
    </div>
  );
});

const CustomTreeItem = forwardRef(function CustomTreeItem(props, ref) {
  return <TreeItem ContentComponent={CustomContent} {...props} ref={ref} />;
});

const getTree = (items, parentField, textField, idField) => {
  return items
    .filter(item => !item[parentField])
    .map((item, index) => (
      <CustomTreeItem
        key={item[idField]}
        nodeId={item[idField]}
        label={item[textField]}
        data-testid={item[idField]}
        ContentProps={{
          sx: { py: 1 },
        }}
      >
        {getSubTree(items, parentField, textField, idField, item[idField])}
      </CustomTreeItem>
    ));
};

const getSubTree = (items, parentField, textField, idField, fatherId) => {
  const children = items.filter(item => item[parentField] === fatherId);

  if (!children.length) return null;

  return children.map(item => (
    <CustomTreeItem
      key={item[idField]}
      nodeId={item[idField]}
      label={item[textField]}
      data-testid={item[idField]}
      ContentProps={{
        sx: { py: 1 },
      }}
    >
      {getSubTree(items, parentField, textField, idField, item[idField])}
    </CustomTreeItem>
  ));
};

export default function GenSelectTree({
  label,
  items,
  parentField = 'parentCompanyId',
  idField = 'companyId',
  textField = 'name',
  onChange,
  selectedItemId,
  size = 'small',
  sx = {},
  disabled = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [expanded, setExpanded] = useState([]);

  const selectedItem = items.find(item => item[idField] === selectedItemId);

  const parentSeeker = useCallback(
    selectId => {
      const found = items.find(item => item[idField] === selectId);
      return found ? found[parentField] : null;
    },
    [idField, items, parentField]
  );

  const handleParents = useCallback(
    (companies, selectedCompany) => {
      if (!selectedCompany) return [];
      const arr = [];
      if (!selectedCompany[parentField]) arr.push(selectedCompany[idField]);
      if (selectedCompany[parentField]) {
        arr.push(parentSeeker(selectedCompany[idField]));
      }
      if (arr.length) arr.push(parentSeeker(arr[0]));
      return arr.filter(Boolean);
    },
    [idField, parentField, parentSeeker]
  );

  useEffect(() => {
    if (!selectedItemId || !selectedItem) return;

    setExpanded(handleParents(items, selectedItem));
  }, [handleParents, items, selectedItem, selectedItemId]);

  return (
    <FormControl fullWidth={true} size={size}>
      <InputLabel
        id="select-tree-label"
        shrink={!!selectedItemId || isOpen}
        disabled={disabled}
      >
        {label}
      </InputLabel>
      <Select
        id="select-tree"
        labelId="select-tree-label"
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        disabled={disabled}
        label={label}
        value={selectedItemId ?? ''}
        sx={{
          ...sx,
          '& > fieldset > legend': {
            ...((!!selectedItemId || isOpen) && {
              maxWidth: '100%',
            }),
          },
          '&.Mui-focused > fieldset > legend': {
            ...(!selectedItemId &&
              isFocused &&
              !isOpen && {
                maxWidth: '0.01px',
              }),
          },
        }}
        MenuProps={{
          sx: {
            '.MuiPaper-root .MuiMenu-list': {
              padding: 0,
            },
          },
        }}
      >
        <MenuItem value={selectedItemId ?? ''} sx={{ display: 'none' }}>
          {(selectedItem || {})[textField]}
        </MenuItem>
        <TreeView
          defaultCollapseIcon={<ExpandMore />}
          defaultExpandIcon={<ChevronRight />}
          onNodeSelect={(data, itemId) => {
            onChange(itemId);
          }}
          selected={selectedItemId}
          sx={{ py: 1, maxHeight: '268px', overflow: 'scroll' }}
          expanded={expanded}
          onNodeToggle={(event, nodeIds) => setExpanded(nodeIds)}
        >
          {getTree(items, parentField, textField, idField)}
        </TreeView>
      </Select>
    </FormControl>
  );
}
